import React, { useCallback } from "react";
import { __ } from "services/Translator";
import {
  TeaserHero,
  TeaserSection,
  TeaserGrid,
  FeatureCard,
} from "components/auth/teaser";
import { STORE_SVG_DEFS } from "./storeSvgDefs";

// Fire the real auth modal the way Header/ExitIntent do: flip the FullLayout
// state on, then release it so it can be re-triggered later.
function pulse(setter) {
  if (typeof setter !== "function") return;
  setter(true);
  window.setTimeout(() => setter(false), 500);
}

function CloverGlyph({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 3.5c2 0 3.5 1.5 3.5 3.5S14 10.5 12 10.5 8.5 9 8.5 7 10 3.5 12 3.5Z"
        fill="currentColor"
        opacity="0.2"
      />
      <path
        d="M10.4 9.6 6.2 7.2c-.5-.3-1.1-.1-1.4.4-.3.5-.1 1.1.4 1.4l4.2 2.4c-.7 1.2-.5 2.8.6 3.7l-2.3 4.1c-.3.5-.1 1.1.4 1.4.5.3 1.1.1 1.4-.4l2.3-4.1c1.1.4 2.4.1 3.2-.8l4.2 2.4c.5.3 1.1.1 1.4-.4.3-.5.1-1.1-.4-1.4l-4.2-2.4c.7-1.2.5-2.8-.6-3.7L18.6 5c.3-.5.1-1.1-.4-1.4-.5-.3-1.1-.1-1.4.4L14.5 8.1c-1.1-.4-2.4-.1-3.2.8Z"
        fill="currentColor"
      />
    </svg>
  );
}

const STEP_ICONS = {
  play: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 8.5 16 12l-6 3.5v-7Z" fill="currentColor" />
    </svg>
  ),
  earn: <CloverGlyph size={22} />,
  redeem: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path
        d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M3 6h18v3H3V6Z" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 6v15" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 6C11 3.5 8.5 3.5 8.5 5.3 8.5 6.6 10 6 12 6Zm0 0c1-2.5 3.5-2.5 3.5-.7C15.5 6.6 14 6 12 6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

// GENERIC, non-personal showcase of what the store holds. No balances, no
// "current" tier, no claim/redeem — every card routes a guest to Sign Up.
const TEASER_SAMPLES = [
  {
    id: "s-vault",
    category: "mystery",
    displayMode: "illust",
    tierClass: "tier-diamond",
    isWide: true,
    artId: "art-vault",
    tag: "VIP DROP",
    tagClass: "vip",
    headline: "UP TO €1,000 CASH",
    title: "THE DIAMOND VAULT",
    desc: "One key, three possible rewards — free spins, bonus cash or real cashback.",
    cost: 8000,
  },
  {
    id: "s-spins",
    category: "free-spins",
    displayMode: "typo",
    artId: "art-spins",
    accentPos: "br",
    tag: "HOT",
    tagClass: "hot",
    headline: "200 FREE SPINS",
    title: "SWEET BONANZA",
    desc: "Top Pragmatic slots, seven-day expiry.",
    bigText: "200",
    bigSize: "xl",
    subLabel: "FREE SPINS",
    cost: 800,
  },
  {
    id: "s-cash",
    category: "cash",
    displayMode: "typo",
    artId: "art-coins",
    accentPos: "tr",
    headline: "€25 BONUS",
    title: "CASH BONUS",
    desc: "Bonus cash with a low 5× wagering.",
    bigText: "€25",
    bigSize: "xl",
    subLabel: "BONUS CASH",
    cost: 2200,
  },
  {
    id: "s-gold",
    category: "mystery",
    displayMode: "illust",
    tierClass: "tier-gold",
    artId: "art-gold",
    tag: "HOT",
    tagClass: "hot",
    headline: "MYSTERY CHEST",
    title: "GOLD CHEST",
    desc: "200–500 free spins or up to €100 in bonus cash.",
    cost: 5000,
  },
  {
    id: "s-race",
    category: "tournaments",
    displayMode: "typo",
    artId: "art-trophy",
    accentPos: "br",
    headline: "€5,000 POOL",
    title: "WEEKLY RACE",
    desc: "Buy in and compete for a weekly cash prize pool.",
    bigText: "€5K",
    bigSize: "xl",
    subLabel: "PRIZE POOL",
    cost: 1500,
  },
  {
    id: "s-cashback",
    category: "vip",
    displayMode: "typo",
    artId: "art-diamond",
    accentPos: "cn",
    tag: "VIP",
    tagClass: "vip",
    headline: "€1,000 CASHBACK",
    title: "REAL CASH",
    desc: "No wagering, paid straight to your balance as real cash.",
    bigText: "€1,000",
    bigSize: "sm",
    subLabel: "REAL CASH",
    cost: 50000,
  },
];

function formatCc(value) {
  return Number(value || 0).toLocaleString();
}

function TeaserArt({ artId, mode }) {
  return (
    <svg
      viewBox="0 0 160 120"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={`store-art-symbol mode-${mode}`}
      style={{ width: "100%", height: "100%", maxHeight: "100%" }}
    >
      <use href={`#${artId}`} />
    </svg>
  );
}

function SampleCardArt({ item }) {
  if (item.displayMode === "illust") {
    return (
      <div
        className={[
          "store-card__image",
          `cat-${item.category}`,
          item.tierClass || "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="store-card__fx" />
        {item.tag ? (
          <span className={`store-card__tag ${item.tagClass || ""}`}>
            {item.tag}
          </span>
        ) : null}
        <div className="store-card__art">
          <TeaserArt artId={item.artId} mode="main" />
        </div>
      </div>
    );
  }
  return (
    <div className={`store-card__image typo cat-${item.category}`}>
      <div className="store-card__fx" />
      {item.tag ? (
        <span className={`store-card__tag ${item.tagClass || ""}`}>
          {item.tag}
        </span>
      ) : null}
      <div
        className={`store-card__accent ${item.accentPos || "cn"}`}
        aria-hidden="true"
      >
        <TeaserArt artId={item.artId} mode="accent" />
      </div>
      <div className={`store-card__big ${item.bigSize || ""}`}>
        {item.bigText}
        <small>{item.subLabel}</small>
      </div>
    </div>
  );
}

function SampleTeaserCard({ item, onActivate }) {
  return (
    <div
      className={[
        "store-card",
        "store-card--teaser",
        `cat-${item.category}`,
        item.isWide ? "wide" : "",
        item.displayMode === "illust" && item.tierClass ? item.tierClass : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="button"
      tabIndex={0}
      aria-label={__(":title — sample reward, sign up to unlock", {
        title: item.title || item.name,
      })}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
    >
      <div className="store-card__content">
        <div className="store-card__headline">{item.headline}</div>
        <div className="store-card__title">{item.title}</div>
        <p className="store-card__desc">{item.desc}</p>
      </div>

      <SampleCardArt item={item} />

      <div className="store-card__bottom">
        <span className="store-card__price">
          <CloverGlyph size={16} />
          <b>{formatCc(item.cost)}</b>
        </span>
        <span className="store-card--teaser__unlock">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
            <rect
              x="4"
              y="11"
              width="16"
              height="9"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M8 11V8a4 4 0 0 1 8 0v3"
              stroke="currentColor"
              strokeWidth="1.8"
            />
          </svg>
          {__("Sign up to unlock")}
        </span>
      </div>
    </div>
  );
}

export default function StoreTeaser(props) {
  const { setModalSignUpPropsData, setModalLoginPropsData } = props;

  const openSignUp = useCallback(
    () => pulse(setModalSignUpPropsData),
    [setModalSignUpPropsData]
  );

  return (
    <div className="store-page store-teaser container-xxl">
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: STORE_SVG_DEFS }}
      />

      <TeaserHero
        align="center"
        className="store-teaser__hero"
        eyebrow={__("The Clover Store")}
        title={__("Turn your play into rewards")}
        subtitle={__(
          "Earn Clover Coins every time you play, then trade them for Free Spins, Bonus Cash, Mystery Chests and real cashback."
        )}
        setModalSignUpPropsData={setModalSignUpPropsData}
        setModalLoginPropsData={setModalLoginPropsData}
        signUpLabel={__("Create free account")}
      >
        <ul className="store-teaser__trust" aria-label={__("Why players trust us")}>
          <li>{__("18+ only")}</li>
          <li>{__("Real cash rewards")}</li>
          <li>{__("Secure & licensed")}</li>
        </ul>
      </TeaserHero>

      <TeaserSection
        align="center"
        eyebrow={__("How it works")}
        title={__("Play. Earn. Redeem.")}
        subtitle={__("Three simple steps — your coins start stacking from your very first game.")}
      >
        <TeaserGrid minColWidth={230} gap={18}>
          <FeatureCard
            icon={STEP_ICONS.play}
            title={__("Play your favourite games")}
            description={__("Every real-money spin earns Clover Coins automatically — no extra steps.")}
          />
          <FeatureCard
            icon={STEP_ICONS.earn}
            title={__("Collect Clover Coins")}
            description={__("Watch your coin balance climb the more you play across the casino.")}
          />
          <FeatureCard
            icon={STEP_ICONS.redeem}
            title={__("Redeem for rewards")}
            description={__("Spend your coins on free spins, bonus cash, mystery chests and real cashback.")}
          />
        </TeaserGrid>
      </TeaserSection>

      <TeaserSection
        align="center"
        eyebrow={__("What's inside")}
        title={__("A taste of the Clover Store")}
        subtitle={__("A sample of the rewards waiting inside. Sign up to unlock the full store.")}
      >
        <div className="store-grid store-teaser__grid">
          {TEASER_SAMPLES.map((item) => (
            <SampleTeaserCard key={item.id} item={item} onActivate={openSignUp} />
          ))}
        </div>
      </TeaserSection>

      <section className="store-teaser__cta">
        <h2 className="store-teaser__cta-title">{__("Ready to start earning?")}</h2>
        <p className="store-teaser__cta-sub">
          {__("Join in under a minute and your Clover Coins start stacking with your very first game.")}
        </p>
        <button
          type="button"
          className="teaser-btn teaser-btn--primary store-teaser__cta-btn"
          onClick={openSignUp}
        >
          {__("Create free account")}
        </button>
      </section>

      <p className="store-teaser__legal">
        {__(
          "18+. Play responsibly. Rewards are subject to the Store terms and applicable wagering requirements, and are available where permitted by law."
        )}
      </p>
    </div>
  );
}
